"use client";

import { useEffect } from "react";
import { useFavProductStore } from "@/features/user/ui/store";
import { useSession } from "@/lib/client/auth/client";
import { useShoppingCartStore } from "@/lib/client/stores";
import { fetcher } from "@/lib/http/fetcher";
import type { CartItemForClient } from "@/lib/shared/types/interfaces";
import { filterPurchasableCartItems } from "@/lib/shared/utils/filterPurchasableCartItems";

const CART_SYNCED_KEY = "cart_synced";

function getHasSyncedCart(userId: string | undefined): boolean {
  if (typeof window === "undefined") return false;
  const stored = sessionStorage.getItem(`${CART_SYNCED_KEY}_${userId || "guest"}`);
  return stored === "true";
}

function setHasSyncedCart(userId: string | undefined, value: boolean): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(`${CART_SYNCED_KEY}_${userId || "guest"}`, value ? "true" : "false");
}

function clearHasSyncedCart(userId: string | undefined): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(`${CART_SYNCED_KEY}_${userId || "guest"}`);
}

function mergeCartItems(localItems: CartItemForClient[], dbItems: CartItemForClient[]) {
  const merged = new Map<string, CartItemForClient>();

  for (const item of [...dbItems, ...localItems]) {
    const key = `${item.product.id}:${item.variant.id}`;
    const existing = merged.get(key);

    if (existing) {
      merged.set(key, {
        ...existing,
        quantity: existing.quantity + item.quantity,
      });
    } else {
      merged.set(key, item);
    }
  }

  return Array.from(merged.values());
}

/**
 * Hook to sync user's favorite products from Payload
 */
export function useSyncUser() {
  const { data: session, isPending } = useSession();
  const { setFavProducts } = useFavProductStore();
  const { setCartItems, setUserId } = useShoppingCartStore();

  useEffect(() => {
    async function syncUserData() {
      if (!session?.user) {
        // Session object without a user is not a sign-out — keep the guest cart.
        setFavProducts([]);
        setUserId(null);
        return;
      }

      // Set userId immediately so cart/favorites persistence works before async sync completes
      const sessionUserId = session.user.id || session.user.email;
      if (sessionUserId) {
        setUserId(sessionUserId);
      }

      try {
        const localCartItems = useShoppingCartStore.getState().cartItems;

        const payloadUser =
          await fetcher<import("@/lib/shared/types/interfaces").UserForClient>("/api/users/me");

        if (!payloadUser) {
          setFavProducts([]);
          setCartItems(filterPurchasableCartItems(localCartItems));
          return;
        }

        setFavProducts(payloadUser.favProducts || []);
        const dbItems = payloadUser.cartItems || [];

        const hasSynced = getHasSyncedCart(sessionUserId);
        let cartItems: CartItemForClient[];
        if (hasSynced) {
          // Subsequent run: replace with server data only (avoid double-count from merge)
          cartItems = dbItems;
        } else {
          // First run: merge guest cart with account cart, or use db if local empty
          cartItems = mergeCartItems(localCartItems, dbItems);
        }
        setHasSyncedCart(sessionUserId, true);
        setCartItems(filterPurchasableCartItems(cartItems));
      } catch (error) {
        console.error("Error syncing user data:", error);
      }
    }

    if (!isPending && session) {
      void syncUserData();
    } else if (!(isPending || session)) {
      // Only clear the client cart on real sign-out (had an account userId).
      // Guests keep in-memory / localStorage cart across remounts and navigations.
      const currentUserId = useShoppingCartStore.getState().userId;
      if (currentUserId) {
        clearHasSyncedCart(currentUserId);
        setFavProducts([]);
        setCartItems([]);
        setUserId(null);
      } else {
        setFavProducts([]);
      }
    }
  }, [session, isPending, setFavProducts, setCartItems, setUserId]);
}
